import { AppNode, NodeType } from "@features/builder/graph/types"
import logger from "@utils/logger"
import { splitLagNodeLabel } from "@utils/node/labelValidation"

export function getMaxLag(
  nodes: {
    type: AppNode | string
    data: { label?: AppNode["data"]["label"] }
  }[],
) {
  let maxLag = 0

  for (const node of nodes) {
    if (node.type !== NodeType.Lag) {
      continue
    }
    const lag = splitLagNodeLabel(node.data.label).lagValue

    if (lag > maxLag) {
      maxLag = lag
    }
  }

  return maxLag
}

export type RollingStartDateOpts = {
  /** We want to get offset from this date. Usually it's today. */
  date: Date
  /** Number of frequencies to go back in time. By default 0 */
  lag?: number
} & (
  | {
      /** Step duration */
      freq: "yearly"
      /** For yearly duration, we can decide when step starts */
      startMonth: number
    }
  | {
      /** Step duration */
      freq: "quarterly" | "monthly"
    }
  | {
      /** Step duration */
      freq: string & {}
    }
)

/**
 * Returns the first day of the month at 00:00:00 UTC.
 * Uses UTC to prevent timezone-related inconsistencies across different user machines.
 */
function getFirstDayOfMonth(date: Date) {
  const year = date.getFullYear()
  const month = date.getMonth()
  return new Date(Date.UTC(year, month, 1))
}

function shiftUTCDate(date: Date, { months = 0, years = 0 }: { months?: number; years?: number }) {
  return new Date(Date.UTC(date.getUTCFullYear() + years, date.getUTCMonth() + months, 1))
}

export function rollingStartDate(opt: RollingStartDateOpts) {
  const { lag = 0 } = opt
  let dateFrom = getFirstDayOfMonth(opt.date)

  switch (opt.freq) {
    case "monthly":
      dateFrom = shiftUTCDate(dateFrom, { months: -lag })
      break
    case "quarterly":
      dateFrom = shiftUTCDate(dateFrom, { months: -lag * 3 })
      break
    case "yearly":
      if ("startMonth" in opt) {
        dateFrom = new Date(Date.UTC(dateFrom.getUTCFullYear(), opt.startMonth - 1, 1))
      }
      dateFrom = shiftUTCDate(dateFrom, { years: -lag })
      break
    default:
      // maybe we should throw?
      logger.warn("Unknown frequency")
  }

  return dateFrom
}

export type RollingEndDateOpts = {
  /** From date. For rolling you can use getFirstDayOfMonth() to determine it. */
  date: Date
  /** Is used to determine end date. It's distance from startDate */
  duration: number
  /** Frequency always starts on 1st of the month */
  freq: "yearly" | "quarterly" | "monthly" | (string & {})
}
export function rollingEndDate(opt: RollingEndDateOpts): Date {
  let dateFrom = getFirstDayOfMonth(opt.date)

  switch (opt.freq) {
    case "monthly":
      dateFrom = shiftUTCDate(dateFrom, { months: opt.duration })
      break
    case "quarterly":
      dateFrom = shiftUTCDate(dateFrom, { months: opt.duration * 3 })
      break
    case "yearly":
      dateFrom = shiftUTCDate(dateFrom, { years: opt.duration })
      break
    default:
      // maybe we should throw?
      logger.warn("Unknown frequency")
  }

  return dateFrom
}
